import { useState, useEffect, useRef } from 'react';
import { OnboardingIntro } from '@/components/OnboardingIntro';
import { Mascot } from '@/components/Mascot';
import EatingTest from '@/screens/EatingTest';
import { buildPortrait } from '@/lib/dofiStart';
import { ONBOARDING_MOLECULE_ENABLED } from '@/lib/flags';
import { EATING_QUESTIONS, FREQUENCY_OPTIONS, STYLE_TEXT, DISCLAIMER, scoreEating, isTestLaunch, type EatingProfile } from '@/lib/eatingProfile';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Send, Check } from 'lucide-react';
import { Button } from '@/components/Button';
import { useUserStore } from '@/store/userStore';
import { supabase } from '@/lib/supabase';
import { ensureSession } from '@/lib/ensureSession';
import { buildDailyTargets } from '@/lib/tdee';
import { useTelegram } from '@/hooks/useTelegram';
import { useLegalAcceptance } from '@/hooks/useLegalAcceptance';
import { LegalAcceptScreen } from '@/components/LegalAcceptScreen';
import type { Sex, Goal, ActivityLevel } from '@/types';
import { fmtNum } from '@/utils/format';
interface Form {
  sex: Sex | null;
  birth_date: string;
  height_cm: string;
  weight_kg: string;
  target_weight_kg: string;
  goal: Goal | null;
  activity_level: ActivityLevel | null;
}
const initial: Form = {
  sex: null,
  birth_date: '',
  height_cm: '',
  weight_kg: '',
  target_weight_kg: '',
  goal: null,
  activity_level: null,
};
interface ChatStep {
  id: keyof Form | 'age' | 'done' | 'eb_intro' | 'eb';
  ebIndex?: number;
  question: string;
  type: 'options' | 'number' | 'text' | 'activity';
  options?: { label: string; value: string }[];
  placeholder?: string;
  unit?: string;
  optional?: boolean;
}
const ACTIVITY_OPTIONS: {
  value: ActivityLevel;
  emoji: string;
  label: string;
  description: string;
  steps: string;
  examples: string;
}[] = [
  {
    value: 'sedentary',
    emoji: '🪑',
    label: 'Малоподвижный',
    description: 'Работа сидя за компом, дома лежу на диване. Никаких тренировок.',
    steps: '3 000 – 5 000 шагов в день',
    examples: 'Программист, бухгалтер, водитель',
  },
  {
    value: 'light',
    emoji: '🚶',
    label: 'Лёгкая активность',
    description: 'Гуляю каждый день 30-60 мин или иногда тренируюсь 1-2 раза в неделю.',
    steps: '7 000 – 10 000 шагов',
    examples: 'Офисный работник который ходит обедать, мама с коляской, лёгкие домашние дела',
  },
  {
    value: 'moderate',
    emoji: '🏃',
    label: 'Умеренная',
    description: 'Тренируюсь регулярно 3-4 раза в неделю или работа на ногах.',
    steps: '10 000+ шагов',
    examples: 'Продавец, тренер раз через день, медсестра, преподаватель',
  },
  {
    value: 'active',
    emoji: '💪',
    label: 'Активный',
    description: 'Тренируюсь 5-6 раз в неделю или физически тяжёлая работа.',
    steps: '12 000+ шагов',
    examples: 'Строитель, грузчик, спортсмен-любитель, курьер',
  },
  {
    value: 'very_active',
    emoji: '🔥',
    label: 'Очень активный',
    description: 'Профессиональный спорт или экстремально физическая работа. Тренировки каждый день.',
    steps: '15 000+ шагов',
    examples: 'Профессиональный спортсмен, шахтёр, лесоруб',
  },
];
const CHAT_STEPS: ChatStep[] = [
  {
    id: 'goal',
    question: 'Какая у тебя главная цель сейчас?',
    type: 'options',
    options: [
      { label: 'Похудеть', value: 'lose' },
      { label: 'Набрать массу', value: 'gain' },
      { label: 'Поддержать форму', value: 'maintain' },
    ],
  },
  {
    id: 'sex',
    question: 'Ты мужчина или женщина?',
    type: 'options',
    options: [
      { label: 'Мужчина', value: 'male' },
      { label: 'Женщина', value: 'female' },
    ],
  },
  {
    id: 'age',
    question: 'Сколько тебе лет?',
    type: 'number',
    placeholder: '36',
  },
  {
    id: 'height_cm',
    question: 'Какой у тебя рост в сантиметрах?',
    type: 'number',
    placeholder: '175',
    unit: 'см',
  },
  {
    id: 'weight_kg',
    question: 'А сколько сейчас весишь?',
    type: 'number',
    placeholder: '70',
    unit: 'кг',
  },
  {
    id: 'target_weight_kg',
    question: 'Какой вес хочешь достичь? (можно пропустить)',
    type: 'number',
    placeholder: '65',
    unit: 'кг',
    optional: true,
  },
  {
    id: 'activity_level',
    question: 'Насколько ты активен в обычной жизни?\nВыбери что лучше описывает твой обычный день — учитывается работа, тренировки, ходьба.',
    type: 'activity',
  },
  // Скрининг пищевого поведения (22.09.2026): 6 вопросов по шкалам DEBQ,
  // можно пропустить. Вопросы и тексты — в src/lib/eatingProfile.ts.
  {
    id: 'eb_intro',
    question: 'И ещё 6 коротких вопросов про то, как ты ешь, — секунд 40. Это поможет понять, с чего нам начать. Правильных ответов нет, отвечай как есть.',
    type: 'options',
    options: [
      { label: 'Давай', value: 'go' },
      { label: 'Пропустить', value: 'skip' },
    ],
  },
  ...EATING_QUESTIONS.map((q, i): ChatStep => ({
    id: 'eb',
    ebIndex: i,
    question: `${i + 1} из ${EATING_QUESTIONS.length}. ${q.text}`,
    type: 'options',
    options: FREQUENCY_OPTIONS,
  })),
  { id: 'done', question: '', type: 'text' },
];
// Пришёл по ссылке теста (лид-магнит): сначала ПОЛНЫЙ тест (33 вопроса,
// экран EatingTest), потом чат с расчётом нормы — уже без короткого скрининга.
function buildSteps(leadTest: boolean): ChatStep[] {
  if (!leadTest) return CHAT_STEPS;
  return CHAT_STEPS.filter((st) => st.id !== 'eb' && st.id !== 'eb_intro');
}
interface BotMsg { role: 'bot' | 'user'; text: string }
/**
 * testMode — прогон из админки (21.09.2026): тот же онбординг целиком, с заставкой
 * и чатом, но в конце НИЧЕГО не пишется в базу. Настоящий прогон перезаписал бы
 * профиль: дату рождения (станет 1 января года по возрасту), рост, вес, цель,
 * нормы, и добавил бы точку в историю веса. Проходить можно сколько угодно раз.
 */
export default function Onboarding({ testMode = false }: { testMode?: boolean } = {}) {
  const nav = useNavigate();

  // Выход из тестового прогона. У настоящего онбординга выхода нет и не нужно,
  // а в тесте без него Слава застревал: ни «назад», ни закрыть админку,
  // только пройти до конца или перезапустить приложение (21.09.2026).
  // Два пути сразу: системная кнопка «Назад» Telegram и своя кнопка на экране
  // (в браузере телеграмной кнопки нет).
  useEffect(() => {
    if (!testMode) return;
    const bb = window.Telegram?.WebApp?.BackButton;
    const back = () => nav('/admin');
    bb?.show();
    bb?.onClick(back);
    return () => {
      bb?.offClick(back);
      bb?.hide();
    };
  }, [testMode, nav]);

  const exitTest = testMode ? (
    <button
      onClick={() => nav('/admin')}
      className="fixed top-3 right-3 z-50 px-3 py-1.5 rounded-lg bg-bg-card/90 border border-line text-xs text-ink-muted active:scale-95 transition-all"
    >
      ✕ Выйти из теста
    </button>
  ) : null;
  const { profile, setProfile } = useUserStore();
  const { haptic } = useTelegram();
  const { needsAcceptance } = useLegalAcceptance();
  const [stepIdx, setStepIdx] = useState(0);
  // Заставка с молекулой перед чатом (21.09.2026). Флаг — откат одной строкой.
  const [intro, setIntro] = useState(ONBOARDING_MOLECULE_ENABLED);
  const [messages, setMessages] = useState<BotMsg[]>([]);
  const [form, setForm] = useState<Form>(initial);
  const [textInput, setTextInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [showFinalSummary, setShowFinalSummary] = useState(false);
  const [ebAnswers, setEbAnswers] = useState<number[]>([]);
  const [eating, setEating] = useState<EatingProfile | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [leadTest] = useState(() => isTestLaunch());
  const [STEPS] = useState(() => buildSteps(leadTest));
  const [fullTestDone, setFullTestDone] = useState(false);
  const currentStep = STEPS[stepIdx];
  useEffect(() => {
    setMessages([
      {
        role: 'bot',
        text: leadTest
          ? `Привет${profile?.first_name ? `, ${profile.first_name}` : ''}! 👋\nРезультат теста я запомнил, дальше буду опираться на него. А теперь посчитаем твою норму — несколько коротких вопросов, около минуты.`
          : `Привет${profile?.first_name ? `, ${profile.first_name}` : ''}! 👋\nЯ Дофи, ученик тренера по здоровью Вячеслава Кокорина, и помогу тебе с питанием. Задам несколько коротких вопросов, чтобы рассчитать твою норму калорий и подобрать рацион.`,
      },
      { role: 'bot', text: STEPS[0].question },
    ]);
  }, []);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, showFinalSummary]);
  function handleAnswer(value: string, label: string) {
    haptic('light');
    setMessages((prev) => [...prev, { role: 'user', text: label }]);
    if (currentStep.id === 'age') {
      const age = Number(value);
      if (!age || age < 5 || age > 120) {
        setMessages((prev) => [...prev, { role: 'bot', text: 'Что-то странное число. Введи возраст от 5 до 120.' }]);
        return;
      }
      const birthYear = new Date().getFullYear() - age;
      setForm((f) => ({ ...f, birth_date: `${birthYear}-01-01` }));
    } else if (
      currentStep.id === 'height_cm' ||
      currentStep.id === 'weight_kg' ||
      currentStep.id === 'target_weight_kg'
    ) {
      setForm((f) => ({ ...f, [currentStep.id]: value }));
    } else if (currentStep.id === 'eb') {
      // Ответы скрининга — в свой массив, не в форму профиля
    } else if (currentStep.id !== 'done' && currentStep.id !== 'eb_intro') {
      setForm((f) => ({ ...f, [currentStep.id]: value as any }));
    }
    setTextInput('');
    // Результат скрининга считаем сразу на последнем вопросе, чтобы показать его
    // сообщением Дофи перед расчётом нормы.
    let result: EatingProfile | null = null;
    if (currentStep.id === 'eb') {
      const next = [...ebAnswers];
      next[currentStep.ebIndex!] = Number(value);
      setEbAnswers(next);
      if (currentStep.ebIndex === EATING_QUESTIONS.length - 1) {
        result = scoreEating(next);
        setEating(result);
      }
    }
    const skipEating = currentStep.id === 'eb_intro' && value === 'skip';
    const nextIdx = skipEating ? STEPS.length - 1 : stepIdx + 1;
    // Портрет из ответов (30.10.2026), а не общий текст типа — см. lib/dofiStart
    const portrait = result ? buildPortrait(result, form.sex) : null;
    const resultMsgs: BotMsg[] = portrait
      ? [{ role: 'bot', text: `${portrait.title}${portrait.intensity ? `\n${portrait.intensity}` : ''}\n\n${portrait.paragraphs.join('\n\n')}\n\n${DISCLAIMER}` }]
      : [];
    setTimeout(() => {
      if (nextIdx >= STEPS.length - 1) {
        setMessages((prev) => [
          ...prev,
          ...resultMsgs,
          { role: 'bot', text: 'Супер, я собрал всё! Сейчас посчитаю твою норму…' },
        ]);
        setStepIdx(nextIdx);
        setShowFinalSummary(true);
      } else {
        const nextStep = STEPS[nextIdx];
        setMessages((prev) => [
          ...prev,
          ...resultMsgs,
          ...(resultMsgs.length ? [{ role: 'bot' as const, text: 'А теперь посчитаем твою норму — ещё несколько вопросов, около минуты.' }] : []),
          { role: 'bot', text: nextStep.question },
        ]);
        setStepIdx(nextIdx);
      }
    }, 400);
  }
  async function finish() {
    if (!profile) return;
    if (testMode) {
      haptic('success');
      alert('Тестовый прогон: ничего не сохранено, профиль не изменился. Дальше — первый разговор с Дофи, как у новичка.');
      try { sessionStorage.setItem('dofi_first_chat', '1'); } catch {}
      nav('/chat?first=preview');
      return;
    }
    setSaving(true);
    try {
      // Гарантируем живую сессию ДО записи — иначе запрос уйдёт анонимом
      // и Postgres вернёт "permission denied for table profiles".
      const authed = await ensureSession();
      if (!authed) {
        haptic('error');
        alert('Сессия потерялась. Обнови страницу и войди заново, потом нажми «Начать» ещё раз.');
        return;
      }
      const targets = buildDailyTargets({
        sex: form.sex!,
        birth_date: form.birth_date,
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        goal: form.goal!,
        activity_level: form.activity_level!,
      });
      const update = {
        sex: form.sex,
        birth_date: form.birth_date,
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        target_weight_kg: form.target_weight_kg ? Number(form.target_weight_kg) : null,
        goal: form.goal,
        activity_level: form.activity_level,
        ...targets,
        // Пропустил скрининг — поле не трогаем.
        ...(eating ? { eating_profile: eating } : {}),
        onboarded: true,
      };
      const { data, error } = await supabase
        .from('profiles')
        .update(update)
        .eq('id', profile.id)
        .select()
        .single();
      if (error) {
        console.error('[Onboarding] profile update error:', JSON.stringify(error));
        throw error;
      }
      try {
        await supabase.from('weight_log').insert({
          user_id: profile.id,
          weight_kg: Number(form.weight_kg),
        });
      } catch (weightErr) {
        console.warn('[Onboarding] weight_log insert failed (non-critical):', weightErr);
      }
      
      // 🆕 ГЕЙМИФИКАЦИЯ — выдаём ачивку за завершение онбординга + первое взвешивание
      try {
        // Ачивка "Начало пути" (100 XP)
        await supabase.rpc('check_achievement', { 
          uid: profile.id, 
          ach_id: 'onboarding_done', 
          new_progress: 1 
        });
        // Ачивка "Контроль веса" (50 XP) — за первое взвешивание
        await supabase.rpc('check_achievement', { 
          uid: profile.id, 
          ach_id: 'first_weight', 
          new_progress: 1 
        });
      } catch (achErr) {
        console.warn('[Onboarding] achievement award failed (non-critical):', achErr);
      }
      
      setProfile(data);
      haptic('success');
      // Онбординг заканчивается разговором с Дофи, а не главным экраном
      // (решение Славы 22.09.2026). Случай 18.09: человек прошёл онбординг,
      // увидел «обычный счётчик калорий» и больше не вернулся. Первым делом
      // спрашиваем, что он хочет поменять, — кольцо калорий подождёт.
      try { sessionStorage.setItem('dofi_first_chat', '1'); } catch {}
      nav('/chat');
    } catch (e: any) {
      console.error('[Onboarding] finish error:', e?.message, e?.code, e?.details);
      haptic('error');
      alert(`Не удалось сохранить. Попробуй ещё раз.\n${e?.message ?? ''}`);
    } finally {
      setSaving(false);
    }
  }
  const preview = (() => {
    if (!form.sex || !form.birth_date || !form.height_cm || !form.weight_kg || !form.goal || !form.activity_level) return null;
    try {
      return buildDailyTargets({
        sex: form.sex,
        birth_date: form.birth_date,
        height_cm: Number(form.height_cm),
        weight_kg: Number(form.weight_kg),
        goal: form.goal,
        activity_level: form.activity_level,
      });
    } catch {
      return null;
    }
  })();
  function skipOptional() {
    if (!currentStep.optional) return;
    handleAnswer('', '— пропустил');
  }
  if (intro) {
    return (
      <>
        {exitTest}
        <OnboardingIntro lead={leadTest} onStart={() => { haptic('light'); setIntro(false); }} />
      </>
    );
  }
  // Порядок входа (решение Славы 22.09.2026): заставка («вау») → согласие одной
  // галочкой → вопросы. Раньше согласие показывалось ПОСЛЕ онбординга, то есть
  // вес, рост и тест пищевого поведения сохранялись до согласия на обработку
  // данных о здоровье. Теперь данные собираются только после согласия.
  if (needsAcceptance) {
    return <LegalAcceptScreen />;
  }
  if (leadTest && !fullTestDone) {
    return (
      <EatingTest
        onDone={() => {
          // Тест из ссылки уже пройден — после онбординга не отправлять на него снова.
          try { sessionStorage.setItem('launch_done', '1'); } catch {}
          setFullTestDone(true);
        }}
      />
    );
  }
  return (
    <div className="flex flex-col h-screen max-w-md mx-auto">
      {exitTest}
      {/* Header */}
      <div className="flex items-center gap-3 px-5 pt-4 pb-3 border-b border-line shrink-0">
        <Mascot pose="calm" compact float={false} className="w-9 h-9 shrink-0" />
        <div>
          <div className="font-display font-bold text-sm">Дофи</div>
          <div className="text-xs text-emerald-glow">онлайн</div>
          {testMode && (
            <div className="text-[10px] text-warn mt-0.5">Тест: ничего не сохраняется</div>
          )}
        </div>
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {messages.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                m.role === 'user'
                  ? 'bg-emerald-glow text-bg rounded-br-md'
                  : 'bg-bg-card border border-line text-ink rounded-bl-md'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{m.text}</p>
            </div>
          </motion.div>
        ))}
        {/* Финальная карточка с КБЖУ */}
        {showFinalSummary && preview && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card-elevated border-emerald-glow/50 p-5 mt-4"
          >
            <div className="text-center mb-4">
              <div className="text-3xl mb-2">🎯</div>
              <div className="text-xs text-ink-dim uppercase tracking-wider mb-1">Твоя дневная норма</div>
              <div className="font-display text-4xl font-bold text-emerald-glow">
                {fmtNum(preview.daily_calories)}
                <span className="text-base text-ink-muted font-body ml-1">ккал</span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs mb-5">
              <div>
                <div className="text-ink-dim mb-0.5">Белки</div>
                <div className="text-emerald-glow font-bold text-base">{preview.daily_protein_g}г</div>
              </div>
              <div>
                <div className="text-ink-dim mb-0.5">Жиры</div>
                <div className="text-warn font-bold text-base">{preview.daily_fat_g}г</div>
              </div>
              <div>
                <div className="text-ink-dim mb-0.5">Углеводы</div>
                <div className="text-sky-glow font-bold text-base">{preview.daily_carbs_g}г</div>
              </div>
            </div>
            <Button fullWidth size="lg" onClick={finish} disabled={saving}>
              {saving ? 'Сохраняем…' : 'Начать! 🚀'}
            </Button>
          </motion.div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Варианты ответа — options */}
      {!showFinalSummary && currentStep?.type === 'options' && (
        <div className="px-3 py-3 border-t border-line space-y-2 shrink-0">
          {currentStep.options!.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleAnswer(opt.value, opt.label)}
              className="w-full py-3 px-4 rounded-xl bg-bg-card border border-line hover:border-emerald-glow/50 hover:bg-emerald-glow/5 transition-all text-left text-sm font-medium"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
      {/* Активность — карточки с описанием */}
      {!showFinalSummary && currentStep?.type === 'activity' && (
        <div className="border-t border-line shrink-0 max-h-[60vh] overflow-y-auto">
          <div className="px-3 pt-3 pb-1 space-y-2">
            {ACTIVITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleAnswer(opt.value, opt.label)}
                className="w-full rounded-xl border border-line bg-bg-card hover:border-emerald-glow/50 hover:bg-emerald-glow/5 transition-all text-left p-3"
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl shrink-0 mt-0.5">{opt.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm mb-0.5">{opt.label}</div>
                    <div className="text-xs text-ink-muted leading-relaxed mb-1.5">{opt.description}</div>
                    <div className="text-xs text-sky-glow mb-1">
                      <span className="mr-1">👟</span>{opt.steps}
                    </div>
                    <div className="text-[10px] text-ink-dim">Примеры: {opt.examples}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          {/* Подсказка */}
          <div className="mx-3 mb-3 mt-1 bg-warn/5 border border-warn/20 rounded-xl px-3 py-2">
            <p className="text-[11px] text-ink-dim leading-relaxed">
              💡 Уровень активности влияет на расчёт твоей дневной нормы калорий. Если выберешь слишком высокий или низкий — нормы могут быть неточными.
            </p>
          </div>
        </div>
      )}
      {/* Числовой ввод */}
      {!showFinalSummary && currentStep?.type === 'number' && (
        <div className="px-3 py-3 border-t border-line shrink-0">
          <div className="flex items-center gap-2">
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              autoFocus
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && textInput) {
                  const display = textInput + (currentStep.unit ? ' ' + currentStep.unit : '');
                  handleAnswer(textInput, display);
                }
              }}
              placeholder={currentStep.placeholder}
              className="input flex-1 text-base py-3"
            />
            {currentStep.optional && (
              <button onClick={skipOptional} className="text-xs text-ink-dim px-2 shrink-0">
                Пропустить
              </button>
            )}
            <button
              onClick={() => {
                if (textInput) {
                  const display = textInput + (currentStep.unit ? ' ' + currentStep.unit : '');
                  handleAnswer(textInput, display);
                }
              }}
              disabled={!textInput}
              className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all shrink-0 ${
                textInput ? 'bg-emerald-glow text-bg active:scale-95' : 'bg-line text-ink-dim'
              }`}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
